// CrossReg Nexus — CAMS Exam Preparation Course
// Original course material © CrossReg Nexus Consultants LLC. Independently developed; not affiliated with
// or endorsed by the body that awards the CAMS certification.
//
// Shared by the training app (viewer, course page) and the API (catalogue titles, certificate rules).

import { M1, M2 } from './cams/m1-2.js';
import { M3, M4 } from './cams/m3-4.js';
import { M5, M6 } from './cams/m5-6.js';
import { M7, M8 } from './cams/m7-8.js';
import { KEY_FACTS } from './cams/keyfacts.js';
import { CASE_STUDIES } from './cams/caseStudies.js';

const SOURCE = [M1, M2, M3, M4, M5, M6, M7, M8];

export const CAMS_PREP_COURSE = {
  id: 'cams-prep',
  title: 'CAMS Exam Preparation Course',
  provider: 'CrossReg Nexus',
  passMark: 80,          // per-module knowledge check
  examPassMark: 75,      // final practice exam
  disclaimer: 'Developed independently by CrossReg Nexus to help you prepare for the CAMS certification exam. Not affiliated with or endorsed by the certifying body; completing it does not confer the CAMS designation.',
};

export const CAMS_PREP_IDS = SOURCE.map(m => m.id);

// Spread correct answers across positions A–D with a fixed (deterministic) shuffle, so the answer
// key never depends on the order options were written in and stays stable between visits.
function seeded(seed) {
  // mulberry32
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}
function shuffleQuestion(q, seed) {
  const rnd = seeded(seed);
  const order = q.options.map((_, i) => i);
  for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [order[i], order[j]] = [order[j], order[i]]; }
  return { ...q, options: order.map(i => q.options[i]), correctAnswer: order.indexOf(q.correctAnswer) };
}

/** Modules in the shape the training viewer renders: content slides followed by one quiz slide. */
export const CAMS_PREP_MODULES = Object.fromEntries(SOURCE.map(m => [m.id, {
  id: m.id,
  number: m.number,
  domain: m.domain,
  title: m.title,
  description: m.description,
  track: 'certification',
  icon: '🎓',
  slides: [
    ...m.slides.map((s, i) => ({ slideNumber: i + 1, ...s })),
    ...(KEY_FACTS[m.id] ? [{
      slideNumber: m.slides.length + 1,
      title: `Module ${m.number} Key Facts`,
      subtitle: 'Review before the knowledge check',
      content: 'Every point below is examinable. Make sure you can explain each one in your own words.',
      keyPoints: KEY_FACTS[m.id],
    }] : []),
    {
      slideNumber: m.slides.length + (KEY_FACTS[m.id] ? 2 : 1),
      title: `Module ${m.number} Knowledge Check`,
      content: `Ten questions on ${m.title}. You need ${CAMS_PREP_COURSE.passMark}% to pass; you can retake it.`,
      isQuiz: true,
      questions: m.quiz.map((q, i) => ({ id: i + 1, ...shuffleQuestion(q, m.number * 1000 + i * 37 + 11) })),
    },
  ],
}]));

/** Hands-on CrossRegGuard labs, keyed by module id (English). */
export const CAMS_PREP_HANDS_ON = Object.fromEntries(SOURCE.map(m => [m.id, m.handsOn]));

/** Case-study questions added to the final practice exam (answer positions shuffled deterministically). */
export const CAMS_PREP_CASE_STUDIES = CASE_STUDIES.map((q, i) => ({ id: `case-${i + 1}`, ...shuffleQuestion(q, 90000 + i * 53 + 7) }));
